<template>
    <div class="search-results-grid" :class="hasContentTypes && 'filtered'" v-resize="handleResize">
      <transition name="fade" mode="out-in" v-on:before-enter="beforeEnter" v-on:after-enter="onAfterEnter">
        <div v-if="isSearching" class="grid-row" key="searching">
            <h4 class="section-title text-center">
                Hämtar kulturarv
                <i class="fa fa-spinner fa-spin" />
            </h4>
        </div>
        <div v-else-if="noSearchResults && isMounted" class="grid-row" key="noSearchResults">
            <h4 class="section-title text-center">
                Inga träffar för {{ searchQuery }}
            </h4>
            <search-tips :noSearchResults="noSearchResults"></search-tips>
        </div>
        <div v-else-if="hasContentTypes" key="resultsFiltered">
          <VirtualCollection
              id="scroll-container"
              v-scroll="handleScroll"
              :cellSizeAndPositionGetter="cellSizeAndPositionGetter"
              :collection="items"
              :width="containerSize.width"
              :height="containerSize.height"
              :containerHeightSpacer="50"
            >
            <div class="item-wrapper" slot="cell" slot-scope="props">
              <div v-if="props.data.header" class="align-items-center" :style="{ display: 'flex', height: '150px' }">
                <h4 class="section-title">{{ getSectionTitle(props.data.contentType) }}</h4>
              </div>
              <result-item v-else :item="props.data" :type="props.data.contentType" v-click="handleItemClick" />
            </div>
          </VirtualCollection>
        </div>
        <div v-else key="results">
            <div class="grid-row" v-if="searchResults && numArticles > 0">
                <h4 class="section-title">{{ getSectionTitle('articles') }}</h4>
                <div class="item-wrapper">
                    <result-item ref="articles" :key="`article_${article.id}`" v-for="article in searchResults.articles.result" :item="article" type="article" />
                    <show-more-item :num-more="numMoreArticles" type-text="artiklar" content-type="articles" />
                </div>
            </div>
            <div class="grid-row" v-if="searchResults && numLocations > 0">
                <h4 class="section-title">{{ getSectionTitle('locations') }}</h4>
                <div class="item-wrapper">
                    <result-item ref="locations" :key="`location_${location.id}`" v-for="location in searchResults.locations.result" :item="location" type="location" />
                    <show-more-item :num-more="numMoreLocations" type-text="platser" content-type="locations" />
                </div>
            </div>
            <div class="grid-row" v-if="searchResults && numImages > 0">
                <h4 class="section-title">{{ getSectionTitle('images') }}</h4>
                <div class="item-wrapper">
                    <result-item ref="images" :key="`image_${image.id}`" v-for="image in searchResults.images.result" :item="image" type="image" />
                    <show-more-item :num-more="numMoreImages" type-text="bilder" content-type="images" />
                </div>
            </div>
            <div class="grid-row" v-if="searchResults && numVideos > 0">
                <h4 class="section-title">{{ getSectionTitle('videos') }}</h4>
                <div class="item-wrapper">
                    <result-item ref="videos" :key="`video_${video.id}`" v-for="video in searchResults.videos.result" :item="video" type="video" />
                    <show-more-item :num-more="numMoreVideos" type-text="filmer" content-type="videos" />
                </div>
            </div>
            <div class="grid-row" v-if="searchResults && numWalks > 0">
                <h4 class="section-title">{{ getSectionTitle('walks') }}</h4>
                <div class="item-wrapper">
                    <result-item ref="walks" :key="`walk_${walk.id}`" v-for="walk in searchResults.walks.result" :item="walk" type="walk" />
                    <show-more-item :num-more="numMoreWalks" type-text="Strövtåg" content-type="walks" />
                </div>
            </div>
            <div class="grid-row" v-if="searchResults && numDocuments > 0">
                <h4 class="section-title">{{ getSectionTitle('documents') }}</h4>
                <div class="item-wrapper">
                    <result-item ref="documents" :key="`document_${document.id}`" v-for="document in searchResults.documents.result" :item="document" type="document" />
                    <show-more-item :num-more="numMoreDocuments" type-text="dokument" content-type="documents" />
                </div>
            </div>
            <div class="grid-row" v-if="searchResults && numByggnadsInventering > 0">
                <h4 class="section-title">{{ getSectionTitle('byggnadsInventering') }}</h4>
                <div class="item-wrapper">
                    <result-item ref="byggnadsInventering" :key="`byggnadsInventering${byggnadsInventering.id}`" v-for="byggnadsInventering in searchResults.byggnadsInventering.result" :item="byggnadsInventering" type="byggnadsInventering" />
                    <show-more-item :num-more="numMoreByggnadsInventering" type-text="inventeringar" content-type="byggnads_inventering" />
                </div>
            </div>
            <div v-if="shouldShowTips">
              <search-tips :noSearchResults="noSearchResults"></search-tips>
            </div>
        </div>
      </transition>
    </div>
</template>

<script>
import Vue from 'vue'
import ResultItem from '../result-item/ResultItem'
import ShowMoreItem from '../result-item/ShowMoreItem'
import { mapState } from 'vuex'

Vue.directive('resize', {
  inserted: function (el, binding) {
    const onResizeCallback = binding.value
    window.addEventListener('resize', () => {
      const width = document.documentElement.clientWidth
      const height = document.documentElement.clientHeight
      onResizeCallback({ width, height })
    })
  }
})

Vue.directive('scroll', {
  inserted: function (el, binding) {
    const onScrollCallback = binding.value
    el.addEventListener('scroll', (event) => {
      const offsetTop = el.scrollTop
      onScrollCallback({ offsetTop, event })
    })
  }
})

Vue.directive('click', {
  inserted: function (el, binding) {
    const onScrollCallback = binding.value
    el.addEventListener('click', () => {
      const offsetTop = el.scrollTop
      onScrollCallback({ offsetTop })
    })
  }
})

let currentRow = 0
let currentColumn = 0

export default {
  components: { ShowMoreItem, ResultItem },
  props: ['searchQuery', 'searchResults', 'searchFilters', 'noSearchResults'],
  data: () => ({
    isMounted: false,
    numMoreArticles: 0,
    numMoreLocations: 0,
    numMoreImages: 0,
    numMoreVideos: 0,
    numMoreDocuments: 0,
    numMoreByggnadsInventering: 0,
    numMoreWalks: 0,
    isSearching: false,
    shouldShowTips: false,
    items: [],
    containerSize: {
      width: 0,
      height: 0
    },
    scrollTop: 0
  }),
  mounted () {
    this.isMounted = true
    // _(this.$refs.articles).filter(article => {
    //   return window.getComputedStyle(article.$el, null).getPropertyValue('display') === 'none'
    // }).size()

    Bus.$on('isSearching', searching => {
      this.isSearching = searching
    })

    this.handleResize({ width: document.documentElement.clientWidth, height: document.documentElement.clientHeight })
  },
  methods: {
    scrollToPosition () {
      const el = document.getElementsByClassName('vue-virtual-collection')[0]
      el?.scrollTo({
        top: this.searchGridScrollPos,
        behavior: 'smooth'
      })
      this.$store.dispatch('setSearchGridScrollPos', 0)
    },
    setupItems () {
      if (!this.isSearching) {
        const dimensions = this.calculateDimensions(this.containerSize.width)
        const filteredSearchResults = this.contentTypes
          .filter(contentType => contentType !== 'audio')
          .map(contentType => {
          return {
            contentType: _.camelCase(contentType),
            result: this.searchResults[_.camelCase(contentType)].result
          }
          })

        this.items = _.flatMap(filteredSearchResults.map(result => {
          const items = result.result.map(item => ({
            data: {
              ...item,
              contentType: result.contentType
            },
            width: dimensions.width,
            height: dimensions.height,
            numColumns: dimensions.numColumns
          }))
          const header = {
            data: {
              contentType: result.contentType,
              header: true
            },
            width: this.containerSize.width,
            height: dimensions.height,
            numColumns: dimensions.numColumns
          }
          return [header, ...items]
        }))
      }
    },
    handleResize: function ({ width, height }) {
      this.setNumMore()

      this.containerSize = {
        width: width - 32,
        height
      }
    },
    handleScroll: function ({ offsetTop }) {
      this.scrollTop = offsetTop
    },
    handleItemClick: function (item) {
      this.$store.dispatch('setSearchGridScrollPos', this.scrollTop)
    },
    calculateDimensions (containerWidth) {
    // Constants for min and max dimensions
      const minWidth = 270
      const maxWidth = 400
      const minHeight = minWidth * 0.5625

      // Calculate maximum number of columns that can fit into the container
      let maxColumns = Math.floor(containerWidth / minWidth)
      if (maxColumns < 1) {
        maxColumns = 1
      }

      // Calculate the item width to fill the container width
      let itemWidth = containerWidth / maxColumns

      // Limit width and height to max dimensions if necessary
      if (itemWidth > maxWidth) {
        itemWidth = maxWidth
        maxColumns = Math.floor(containerWidth / itemWidth)
      }

      let itemHeight = itemWidth * 0.5625

      // Limit width and height to min dimensions if necessary
      if (itemWidth < minWidth) {
        itemWidth = minWidth
        itemHeight = minHeight
        maxColumns = Math.floor(containerWidth / itemWidth)
      }

      return {
        width: itemWidth,
        height: itemHeight,
        numColumns: maxColumns
      }
    },
    cellSizeAndPositionGetter (item, index) {
      if (index === 0) {
        currentRow = 0
        currentColumn = 0
      }

      if (item.data.header && currentColumn !== 0) {
        // If the item is a header and we're not at the start of a row, start a new row
        currentColumn = 0
        currentRow++
      }

      const posData = {
        width: item.width,
        height: item.height,
        x: currentColumn * item.width,
        y: currentRow * item.height
      }

      // After placing an item, prepare for the next one:
      currentColumn++

      if (item.data.header || currentColumn >= item.numColumns) {
        // If the item is a header, or we've reached the end of a row, start a new row
        currentColumn = 0
        currentRow++
      }

      return posData
    },
    isInViewport: (elem) => {
      const bounding = elem.getBoundingClientRect()
      return (
        bounding.top >= 0 &&
          bounding.left >= 0 &&
          bounding.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
          bounding.right <= (window.innerWidth || document.documentElement.clientWidth)
      )
    },

    getSingularPlural (type) {
      switch (type) {
        case 'articles':
          return ['artikel', 'artiklar']
        case 'locations':
          return ['plats', 'platser']
        case 'images':
          return ['bild', 'bilder']
        case 'videos':
          return ['video', 'videor']
        case 'documents':
          return ['dokument', 'dokument']
        case 'byggnadsInventering':
          return ['byggnadsinventering', 'byggnadsinventeringar']
        case 'walks':
          return ['Strövtåg', 'Strövtåg']
        default:
          return ['', '']
      }
    },

    getSectionTitle (type) {
      const singularPlural = this.getSingularPlural(type)
      const count = this[`num${_.upperFirst(type)}`]
      if (this.searchQuery !== '' || this.hasFilters) {
        return `${count} matchande ${count === 1 ? singularPlural[0] : singularPlural[1]}`
      } else {
        return `Alla ${count} ${singularPlural[1]}`
      }
    },

    setNumMore () {
      this.$nextTick(function () {
        this.numMoreArticles = _.isArray(this.$refs.articles) ? this.numArticles - _(this.$refs.articles).filter(article => {
          return window.getComputedStyle(article.$el, null).getPropertyValue('display') !== 'none'
        }).size() : 0
        this.numMoreLocations = _.isArray(this.$refs.locations) ? this.numLocations - _(this.$refs.locations).filter(location => {
          return window.getComputedStyle(location.$el, null).getPropertyValue('display') !== 'none'
        }).size() : 0
        this.numMoreImages = _.isArray(this.$refs.images) ? this.numImages - _(this.$refs.images).filter(image => {
          return window.getComputedStyle(image.$el, null).getPropertyValue('display') !== 'none'
        }).size() : 0
        this.numMoreVideos = _.isArray(this.$refs.videos) ? this.numVideos - _(this.$refs.videos).filter(video => {
          return window.getComputedStyle(video.$el, null).getPropertyValue('display') !== 'none'
        }).size() : 0
        this.numMoreDocuments = _.isArray(this.$refs.documents) ? this.numDocuments - _(this.$refs.documents).filter(document => {
          return window.getComputedStyle(document.$el, null).getPropertyValue('display') !== 'none'
        }).size() : 0
        this.numMoreByggnadsInventering = _.isArray(this.$refs.byggnadsInventering) ? this.numByggnadsInventering - _(this.$refs.byggnadsInventering).filter(byggnadsInventering => {
          return window.getComputedStyle(byggnadsInventering.$el, null).getPropertyValue('display') !== 'none'
        }).size() : 0
        this.numMoreWalks = _.isArray(this.$refs.walks) ? this.numWalks - _(this.$refs.walks).filter(walk => {
          return window.getComputedStyle(walk.$el, null).getPropertyValue('display') !== 'none'
        }).size() : 0
      })
    },

    beforeEnter () {
      this.setNumMore()

      this.shouldShowTips = !this.searchQuery && !this.hasFilters && !this.hasContentTypes
    },

    onAfterEnter () {
      this.scrollToPosition(this.searchGridScrollPos)
    }
  },
  watch: {
    searchResults: function () {
      const sumNum = this.numArticles + this.numLocations + this.numImages + this.numVideos + this.numDocuments + this.numWalks
      const sumNumMore = this.numMoreArticles + this.numMoreLocations + this.numMoreImages + this.numMoreVideos + this.numMoreDocuments + this.numMoreWalks
      if (sumNum > 0 && sumNumMore === 0) {
        this.setNumMore()
      }

      this.setupItems()
    },
    containerSize: function () {
      if (!this.searchResults.byggnadsInventering) {
        this.items = []
      } else {
        this.setupItems()
      }
    }
  },
  computed: {
    numArticles: function () {
      return _.get(this.searchResults, 'articles.count') || 0
    },
    numLocations: function () {
      return _.get(this.searchResults, 'locations.count') || 0
    },
    numImages: function () {
      return _.get(this.searchResults, 'images.count') || 0
    },
    numVideos: function () {
      return _.get(this.searchResults, 'videos.count') || 0
    },
    numDocuments: function () {
      return _.get(this.searchResults, 'documents.count') || 0
    },
    numByggnadsInventering: function () {
      return _.get(this.searchResults, 'byggnadsInventering.count') || 0
    },
    numWalks: function () {
      return _.get(this.searchResults, 'walks.count') || 0
    },
    ...mapState({
      contentTypes: state => state.search.filtered.contentTypes,
      searchGridScrollPos: state => state.ui.searchGridScrollPos
    }),
    searchQueryValue: function () {
      return this.searchQuery ? `för ${this.searchQuery}` : ''
    },
    hasContentTypes: function () {
      return _.size(this.contentTypes) > 0
    },
    hasFilters: function () {
      return _(this.searchFilters).filter((item, key) => {
        return key !== 'contentTypes'
      }).flatMap().size() > 0
    }
  }
}
</script>
